import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/sonner';
import { SEO } from '@/components/SEO';
import { PasswordStrengthMeter } from '@/components/PasswordStrengthMeter';
import { MfaSetupCard } from '@/components/security/MfaSetupCard';
import { changeClientPassword, apiErrorMessage } from '@/lib/api';
import { isPasswordStrongEnough } from '@/lib/passwordStrength';
import { CLIENT_SECURITY_PAGE } from '@/constants/testIds';

export default function ClientAccountSecurity() {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isPasswordStrongEnough(newPassword)) {
      toast.error('Please meet all the password requirements below.');
      return;
    }
    setLoading(true);
    try {
      await changeClientPassword(currentPassword, newPassword);
      toast.success('Password updated successfully.');
      setCurrentPassword('');
      setNewPassword('');
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Failed to update password.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] bg-[#0A0A0A] px-4 py-16" data-testid={CLIENT_SECURITY_PAGE.root}>
      <SEO title="Account Security" description="Manage your password and two-factor authentication." />
      <div className="mx-auto max-w-4xl">
        <Link
          to="/client"
          data-testid={CLIENT_SECURITY_PAGE.backLink}
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.12em] text-[hsl(var(--lxra-muted))] hover:text-[hsl(var(--lxra-gold))] transition-colors mb-8"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to my galleries
        </Link>

        <p className="text-xs uppercase tracking-[0.18em] text-[hsl(var(--lxra-gold))]">Account</p>
        <h1 className="font-display text-3xl tracking-[0.03em] text-[hsl(var(--lxra-fg))] mt-1 mb-10">Security</h1>

        <div className="flex flex-col gap-8">
          <div className="max-w-md rounded-2xl border border-[hsl(var(--lxra-border))] bg-[#0F0F10] p-8">
            <h3 className="font-display text-xl text-[hsl(var(--lxra-fg))] mb-1">Change Password</h3>
            <p className="text-sm text-[hsl(var(--lxra-muted))] mb-6">Update your login password.</p>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid={CLIENT_SECURITY_PAGE.changePasswordForm}>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-[0.1em] text-[hsl(var(--lxra-muted))]">Current Password</Label>
                <Input
                  type="password"
                  required
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  data-testid={CLIENT_SECURITY_PAGE.currentPasswordInput}
                  className="bg-[#141416] border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))] h-10 focus-visible:ring-[hsl(var(--lxra-gold))]"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-[0.1em] text-[hsl(var(--lxra-muted))]">New Password</Label>
                <Input
                  type="password"
                  required
                  minLength={10}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  data-testid={CLIENT_SECURITY_PAGE.newPasswordInput}
                  className="bg-[#141416] border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))] h-10 focus-visible:ring-[hsl(var(--lxra-gold))]"
                />
                <PasswordStrengthMeter password={newPassword} testIdPrefix={CLIENT_SECURITY_PAGE.changePasswordForm} />
              </div>
              <Button
                type="submit"
                disabled={loading}
                data-testid={CLIENT_SECURITY_PAGE.submitButton}
                className="h-10 px-5 rounded-lg bg-[hsl(var(--lxra-gold))] text-[#0A0A0A] hover:bg-[hsl(var(--lxra-gold-hover))] disabled:opacity-60 text-xs uppercase tracking-[0.1em] font-semibold"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Update Password'}
              </Button>
            </form>
          </div>

          <MfaSetupCard />
        </div>
      </div>
    </div>
  );
}
